import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import {
  api,
  type AllianceHub,
  type AllianceHubMegaExpressWindow,
} from '@/lib/api';
import PlayerHubHeader from '@/components/PlayerHubHeader';
import { usePlayerAlliance } from '@/lib/usePlayerAlliance';
import {
  AboveWeightSection,
  BusterWeekSection,
  GlowUpSection,
  IronThumbsSection,
  LastMegaExpressSection,
  SectionTitle,
  SleepingBeautySection,
  VsSlackersSection,
  WorstParkerSection,
  playerLabel,
} from './PlayerHubAlliance';

export default function PlayerHubHallOfFame() {
  const { token = '' } = useParams<{ token: string }>();

  const q = useQuery({
    queryKey: ['alliance-hub', token],
    queryFn: () => api.getAllianceHub(token),
    enabled: !!token,
  });
  const alliance = usePlayerAlliance(token);
  const allianceLabel = alliance ? alliance.tag || alliance.name : '';

  if (q.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10 text-slate-400">
          Loading…
        </div>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10">
          <h1 className="mb-2 text-xl font-semibold">Link not valid</h1>
          <p className="text-sm text-slate-400">
            This link is invalid or has been revoked.
          </p>
        </div>
      </div>
    );
  }
  const data: AllianceHub = q.data;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <PlayerHubHeader
        token={token}
        playerLabel={allianceLabel}
        playerRank=""
        subtitle="Hall of Fame"
      />
      <main className="mx-auto max-w-md space-y-6 px-4 py-6">
        <MegaExpressLast7dSection data={data.megaExpressLast7d} />
        <IronThumbsSection data={data.ironThumbs} />
        <GlowUpSection data={data.glowUp} />
        <AboveWeightSection data={data.aboveWeight} />
        <div className="grid grid-cols-2 gap-2">
          <LastMegaExpressSection data={data.lastMegaExpress} />
          <WorstParkerSection pick={data.worstParker} />
          <BusterWeekSection data={data.busterWeek} />
          <SleepingBeautySection data={data.sleepingBeauty} />
          <VsSlackersSection slackers={data.vsUnderperformers} />
        </div>
      </main>
    </div>
  );
}

function MegaExpressLast7dSection({
  data,
}: {
  data: AllianceHubMegaExpressWindow | null;
}) {
  if (!data) return null;
  return (
    <section className="space-y-2">
      <SectionTitle
        icon={<Sparkles size={14} />}
        label="Mega-Express · past 7 days"
      />
      <div className="rounded-lg border border-emerald-800/60 bg-emerald-950/20 px-4 py-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 tabular-nums">
          {data.weekStart} → {data.weekEnd}
        </div>
        <div className="mt-1 flex items-baseline justify-between gap-2">
          <span className="text-base font-semibold text-emerald-100">
            {data.total} Mega-Express{data.total === 1 ? '' : 'es'} bought
          </span>
        </div>
        {data.top.length === 0 ? (
          <div className="mt-1 text-[11px] italic text-slate-400">
            No buyers last week.
          </div>
        ) : (
          <ul className="mt-2 space-y-0.5 text-[11px]">
            {data.top.map((e, i) => (
              <li
                key={e.player.id}
                className="flex items-baseline justify-between gap-2 text-slate-300"
              >
                <span>
                  <span className="mr-1 text-slate-500 tabular-nums">
                    {i + 1}.
                  </span>
                  {playerLabel(e.player)}
                </span>
                <span className="tabular-nums text-emerald-200">
                  {e.count}×
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
